import i18n from 'scoreboard/components/i18n'
import * as formatValue from 'scoreboard/components/format-value'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import currency from 'scoreboard/components/currency'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, CURRENCY, PERCENT } = DataType

/**
 * Returns a potentially-shortened formatter appropriate for the given calculation type
 *
 * @param type a type from InitiativeCalculationType
 * @param constantOptions options that should be used on each invocation of the
 * 						  returned formatter
 */
function formatInitiativeCalculations (type, constantOptions) {
	let fmt = shortenedAndUnshortened(type)
	/**
	 * @param {Number} val the value to format
	 * @param {Object} [opt] additional formatting options to apply
	 */
	return function (val, opt) {
		let options = _.extend({}, constantOptions, opt)

		return fmt(val, options).shortened
	}
}

/**
 * Returns a pair of formatters (shortened and unshortened) appropriate for the
 * given calculation type.
 *
 * @param type a type from InitiativeCalculationType
 * @param constantOptions options that should be used on each invocation of the
 * 						  returned formatters
 */
function shortenedAndUnshortened (type, constantOptions) {
	let fmt = formatters[type] || basic.bind(null, type)

	return function (val, opt) {
		let options = _.extend({}, constantOptions, opt)

		return fmt(val, options)
	}
}

function basic (typeCode, val, opt) {
	return formatValue.shortenedAndUnshortened(_.extend({
		value: val,
		dataType: dataTypeForInitCalcType(typeCode)
	}, opt))
}

/** Returns the data type for the given initiative calculation type */
var dataTypeForInitCalcType = _.memoize(function (calcType) {
	return constants.initiativeCalculationTypes[calcType].dataType
})

var formatters = {
	'budget-variance': budgetVariance.bind(null, CURRENCY),
	'budget-variance-percent': budgetVariance.bind(null, PERCENT),
	'schedule-variance': scheduleVariance,
	'currency': (_, { currencyCode }) => {
		const label = currency.label(currencyCode)
		return {
			shortened: label,
			unshortened: label
		}
	}
}

function budgetVariance (dataType, val, opt) {
	if (_.isNil(val)) return { shortened: '', unshortened: '' }

	var formatted = formatValue.shortenedAndUnshortened(_.extend({
		value: Math.abs(val),
		dataType
	}, opt))

	return {
		shortened: i18n.get(budgetMessageCodes[budgetStatus(val)], formatted.shortened),
		unshortened: i18n.get(budgetMessageCodes[budgetStatus(val)], formatted.unshortened)
	}
}

function budgetStatus (variance) {
	if (variance === 0) {
		return 'on'
	}

	return variance > 0 ? 'under' : 'over'
}

var budgetMessageCodes = {
	on: 'initiatives.onBudget',
	under: 'initiatives.underBudget',
	over: 'initiatives.overBudget'
}

function scheduleVariance (val, opt) {
	if (_.isNil(val)) return { shortened: '', unshortened: '' }

	var formatted = formatValue.shortenedAndUnshortened(_.extend({
		value: Math.abs(val),
		dataType: NUMBER
	}, opt))

	var status = scheduleStatus(val)
	if (Math.abs(val) === 1) {
		status += 'One'
	}

	return {
		shortened: i18n.get(scheduleMessageCodes[status], formatted.shortened),
		unshortened: i18n.get(scheduleMessageCodes[status], formatted.unshortened)
	}
}

function scheduleStatus (variance) {
	if (variance === 0) {
		return 'on'
	}

	return variance > 0 ? 'ahead' : 'behind'
}

var scheduleMessageCodes = {
	on: 'initiatives.onSchedule',
	ahead: 'initiatives.daysEarly',
	behind: 'initiatives.daysLate',
	aheadOne: 'initiatives.dayEarly',
	behindOne: 'initiatives.dayLate'
}

export default formatInitiativeCalculations
export { shortenedAndUnshortened, dataTypeForInitCalcType }

formatInitiativeCalculations.__filename__ = 'initiatives/format-initiative-calculations.js'
